import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import GoogleButton from "@/components/GoogleButton";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1447933601403-0c6688de566e";

export default function Auth() {
  useEffect(() => window.scrollTo(0, 0), []);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/cont");
  }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success("Bine ai venit!");
      navigate("/cont");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-20 grid lg:grid-cols-2 min-h-screen">
      <div className="relative hidden lg:block bg-ink overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-16">
          <p className="overline text-sand mb-4">Coffee Cup Supply</p>
          <h2 className="font-display text-cream text-6xl tracking-tight leading-[1]">
            Contul tău de cafea, într-un singur loc.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <div className="flex gap-6 mb-10">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                data-testid={`auth-tab-${m}`}
                className={`font-display text-4xl tracking-tight transition-colors ${
                  mode === m ? "text-ink" : "text-ink/30 hover:text-ink/60"
                }`}
              >
                {m === "login" ? "Autentificare" : "Cont nou"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <Field label="Nume complet" value={form.name} onChange={set("name")} testid="auth-name" />
            )}
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="auth-email" />
            <Field label="Parolă" type="password" value={form.password} onChange={set("password")} testid="auth-password" />

            {error && <p className="text-destructive text-sm" data-testid="auth-error">{error}</p>}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full bg-ink text-cream py-4 hover:bg-roast transition-colors text-sm font-semibold tracking-wide disabled:opacity-60"
            >
              {busy ? "Se procesează..." : mode === "login" ? "Intră în cont" : "Creează cont"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <span className="h-px bg-line flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">sau</span>
            <span className="h-px bg-line flex-1" />
          </div>
          <GoogleButton next="/cont" />

          <p className="text-sm text-muted-foreground mt-6">
            {mode === "login" ? "Nu ai cont? " : "Ai deja cont? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-roast border-b border-roast" data-testid="auth-switch">
              {mode === "login" ? "Creează unul" : "Autentifică-te"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="overline text-ink/50">{label}</label>
    <input required type={type} value={value} onChange={onChange} data-testid={testid} className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors" />
  </div>
);
